import { useCallback, useEffect, useState, useRef } from 'react'
import { UseImageProps, UseImageResult } from '~/app/features/Gallery/components/DesignTile/interface'
import { instrumenter } from '~/app/utils/instrumentation'
import { useCallbackRef } from '~/app/features/Gallery/hooks/useRef'
import { useTranslations } from '~/app/features/Gallery/hooks/useTranslations'
import { useDebouncedCallback } from 'use-debounce'
import { LOADING_TYPE } from '~/app/constants'
import { useSelector } from 'react-redux'
import { stringRenderPropertySelector } from '~/app/store/config/reducer'
import { RenderProperty } from 'shared/renderProperties'
import { dpcsLoadingSelector } from '~/app/store/designPersonalization/selectors'
import { resolveSrc, resolveSrcSet } from '~/app/utils/imageUtils'

export const useImage = ({ imageProps }: UseImageProps): UseImageResult => {
  const localize = useTranslations()
  const stringRenderProperty = useSelector(stringRenderPropertySelector)
  const tilePreviewSize = stringRenderProperty(RenderProperty.TilePreviewSize) || ''
  const dpcsLoading = useSelector(dpcsLoadingSelector)

  const { previewsUrls, alt = '', loading = LOADING_TYPE.LAZY, srcSet, bypassedApproval, fetchpriority, isLoading: isImageLoading } = imageProps

  const imageSrc = resolveSrc(previewsUrls?.size1x) || ''
  const imageSrcSet = resolveSrcSet(previewsUrls, srcSet) || ''

  const isLazyLoading = loading === LOADING_TYPE.LAZY
  const [isLoading, setIsLoading] = useState<boolean>(isLazyLoading)
  const [currentSrcSet, setCurrentSrcSet] = useState<string>(imageSrcSet)
  const [showLoader, setShowLoader] = useState<boolean>(true)
  const [isError, setIsError] = useState<boolean>(false)

  // Latch: set true when dpcsLoading starts, only release on image onLoad
  const [awaitingPersonalizedImage, setAwaitingPersonalizedImage] = useState<boolean>(false)
  const prevDpcsLoading = useRef<boolean>(dpcsLoading)

  // When dpcsLoading goes from false to true, set the latch
  // eslint-disable-next-line react-hooks/refs
  if (dpcsLoading && !prevDpcsLoading.current) {
    setAwaitingPersonalizedImage(true)
  }
  // eslint-disable-next-line react-hooks/refs
  prevDpcsLoading.current = dpcsLoading

  const debouncedLoaded = useDebouncedCallback(() => {
    setIsError(false)
    setIsLoading(false)
  }, 50)

  const debouncedShowLoader = useDebouncedCallback(() => {
    setShowLoader(isLoading)
  }, 100)

  const imgRef = useCallbackRef<HTMLImageElement>(null, (current) => {
    if (current?.complete && current?.naturalWidth === 0) {
      setIsError(true)
    }
    setIsLoading(!current?.complete)
  })

  useEffect(() => {
    const newSrcSet = resolveSrcSet(previewsUrls, srcSet) || ''

    if (newSrcSet !== currentSrcSet) {
      setIsLoading(true)
      setIsError(false)
      setCurrentSrcSet(newSrcSet)
    }
  }, [currentSrcSet, srcSet, previewsUrls])

  useEffect(() => {
    if (isLoading) {
      debouncedShowLoader()
    } else {
      setShowLoader(false)
    }
  }, [isLoading, debouncedShowLoader])

  const onError = useCallback((): void => {
    if (!bypassedApproval) {
      instrumenter.recordImageFailure({
        srcSet,
        ...previewsUrls,
      })
    }

    setIsError(true)
    setIsLoading(false)
    setAwaitingPersonalizedImage(false)
  }, [bypassedApproval, srcSet, previewsUrls])

  const onLoad = (): void => {
    setAwaitingPersonalizedImage(false)
    debouncedLoaded()
  }

  const isEagerLoading = LOADING_TYPE.EAGER && isLoading
  const showSpinner = isImageLoading || (showLoader && (isLazyLoading || isEagerLoading)) || awaitingPersonalizedImage

  return {
    imgRef,
    showSpinner,
    alt,
    imageSrc,
    imageSrcSet,
    loading,
    onLoad,
    onError,
    accessibilityText: localize('Loading'),
    isError: isError && !isImageLoading,
    fetchpriority,
    size: tilePreviewSize,
    emptyPreviewText: localize('NoPreviewAvailable'),
  }
}
